import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Chip, MenuItem, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, IconButton, CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SupplierService } from '../../services/supplier.service';
import { SupplierItemService } from '../../services/supplierItem.service';
import { ItemService } from '../../services/item.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { AuthService } from '../../services/auth.service';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { SupplierItem } from '../../interfaces/procurement/supplierItem.interface';
import { Item } from '../../interfaces/item.interface';
import { PurchaseOrderCreateDTO } from '../../interfaces/procurement/purchaseOrder.interface';
import { useToast } from '../../context/ToastContext';

interface LineDraft {
  itemId: string;
  quantity: number;
  unitPrice: number;
  isFromCatalogue: boolean;
}

const emptyLine = (): LineDraft => ({ itemId: '', quantity: 1, unitPrice: 0, isFromCatalogue: false });

export const PurchaseOrderCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillSupplierId = searchParams.get('supplierId') || '';
  const prefillItemId = searchParams.get('itemId') || '';
  const toast = useToast();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [catalogueMap, setCatalogueMap] = useState<Map<string, SupplierItem>>(new Map());
  const [form, setForm] = useState({
    supplierId: prefillSupplierId,
    orderDate: new Date().toISOString().slice(0, 10),
    expectedDate: '',
    notes: '',
    taxAmount: 0,
  });
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [itemPrefillApplied, setItemPrefillApplied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      SupplierService.listByInstitution(0, 500).then(r => r.content),
      ItemService.getItemsByInstitution(0, 500).then(r => r.content),
    ]).then(([sups, its]) => { setSuppliers(sups); setItems(its); })
      .catch(e => toast.showApiError(e, 'Failed to load data'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When supplier changes, fetch catalogue
  useEffect(() => {
    if (!form.supplierId) { setCatalogueMap(new Map()); return; }
    SupplierItemService.listBySupplier(form.supplierId)
      .then(rows => {
        const map = new Map(rows.map(r => [r.itemId, r]));
        setCatalogueMap(map);
        if (prefillItemId && !itemPrefillApplied) {
          const cat = map.get(prefillItemId);
          setLines([{
            itemId: prefillItemId,
            quantity: 1,
            unitPrice: cat ? cat.unitPrice : 0,
            isFromCatalogue: !!cat,
          }]);
          setItemPrefillApplied(true);
        }
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier catalogue'));
  }, [form.supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = useMemo(() => lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0), [lines]);
  const total = subtotal + form.taxAmount;

  const updateLine = (idx: number, patch: Partial<LineDraft>) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const onItemPick = (idx: number, itemId: string) => {
    const cat = catalogueMap.get(itemId);
    if (cat) updateLine(idx, { itemId, unitPrice: cat.unitPrice, isFromCatalogue: true });
    else updateLine(idx, { itemId, isFromCatalogue: false });
  };

  const onPriceEdit = (idx: number, val: number) => updateLine(idx, { unitPrice: val, isFromCatalogue: false });

  const handleSubmit = async () => {
    if (!form.supplierId) { toast.showApiError(null, 'Pick a supplier'); return; }
    if (lines.some(l => !l.itemId || l.quantity <= 0 || l.unitPrice <= 0)) {
      toast.showApiError(null, 'Every line needs an item, quantity, and price'); return;
    }
    const dto: PurchaseOrderCreateDTO = {
      supplierId: form.supplierId,
      orderDate: form.orderDate,
      expectedDate: form.expectedDate || undefined,
      notes: form.notes || undefined,
      taxAmount: form.taxAmount,
      institutionId: AuthService.getSelectedInstitution()!!,
      lines: lines.map(l => ({ itemId: l.itemId, quantity: l.quantity, unitPrice: l.unitPrice })),
    };
    try {
      setSaving(true);
      const po = await PurchaseOrderService.create(dto);
      toast.showSuccess('Purchase order created');
      navigate(`/procurement/purchase-orders/${po.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create purchase order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>New Purchase Order</Typography>
      <Paper sx={{ p: 3, mb: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        <TextField
          select
          label="Supplier"
          required
          value={form.supplierId}
          onChange={e => setForm({ ...form, supplierId: e.target.value })}
          inputProps={{ 'aria-label': 'Supplier' }}
        >
          {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
        </TextField>
        <TextField
          label="Order Date"
          type="date"
          value={form.orderDate}
          onChange={e => setForm({ ...form, orderDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Expected Date"
          type="date"
          value={form.expectedDate}
          onChange={e => setForm({ ...form, expectedDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Tax Amount"
          type="number"
          value={form.taxAmount}
          onChange={e => setForm({ ...form, taxAmount: Number(e.target.value) })}
          inputProps={{ 'aria-label': 'Tax Amount' }}
        />
        <TextField
          sx={{ gridColumn: '1 / -1' }}
          label="Notes"
          multiline
          rows={2}
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
        />
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lines</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Unit Price</TableCell>
              <TableCell>Line Total</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Item"
                    value={l.itemId}
                    onChange={e => onItemPick(idx, e.target.value)}
                    inputProps={{ 'aria-label': 'Item' }}
                  >
                    {items.map(i => <MenuItem key={i.id} value={i.id}>{i.itemName}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    label="Quantity"
                    value={l.quantity}
                    onChange={e => updateLine(idx, { quantity: Number(e.target.value) })}
                    inputProps={{ 'aria-label': 'Quantity' }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      type="number"
                      size="small"
                      label="Unit Price"
                      value={l.unitPrice}
                      onChange={e => onPriceEdit(idx, Number(e.target.value))}
                      inputProps={{ 'aria-label': 'Unit Price' }}
                    />
                    {l.isFromCatalogue && <Chip label="from catalogue" size="small" />}
                  </Box>
                </TableCell>
                <TableCell>{(l.quantity * l.unitPrice).toFixed(4)}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => setLines(ls => ls.filter((_, i) => i !== idx))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button startIcon={<AddIcon />} onClick={() => setLines(ls => [...ls, emptyLine()])}>Add line</Button>
      </Paper>

      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        <Typography>Subtotal: {subtotal.toFixed(4)}</Typography>
        <Typography>Tax: {form.taxAmount.toFixed(4)}</Typography>
        <Typography variant="h6">Total: {total.toFixed(4)}</Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>Create PO</Button>
      </Box>
    </Box>
  );
};

export default PurchaseOrderCreate;
